// src/sections/Credits.jsx — member / tenant-admin credits view (route /credits).
// SA in adminMode is bounced to /admin (this view is tenant-scoped). A hybrid SA
// NOT in adminMode views their active tenant here and sees money.
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchCreditBalance, fetchCreditUsage } from '../services/api/creditsApi.js';
import CreditBalanceCard from '../components/credits/CreditBalanceCard.jsx';
import CreditUsageTable from '../components/credits/CreditUsageTable.jsx';

const FONT = "'DM Sans', sans-serif";
const COLOR = { text: '#1d1d1f', muted: '#6c6c6d', errFg: '#EF4444' };

export default function Credits() {
  const { activeTenant, isSuperadmin, adminMode } = useAuth();
  const tenantId = activeTenant?.tenant_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (isSuperadmin && adminMode) return undefined; // bounced below
    if (!tenantId) { setLoading(false); return undefined; } // no active tenant → don't spin forever

    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.allSettled([
      fetchCreditBalance({ tenant: tenantId }),
      fetchCreditUsage({ tenant: tenantId }),
    ]).then((results) => {
      if (cancelled) return;
      const [b, u] = results;
      setBalance(b.status === 'fulfilled' ? b.value : null);
      setUsage(u.status === 'fulfilled' ? u.value : null);
      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length === results.length) {
        setError(rejected[0].reason?.message || 'Errore nel caricamento dei crediti');
      } else if (rejected.length > 0) {
        setError('Alcuni dati sui crediti non sono disponibili');
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [isSuperadmin, adminMode, tenantId]);

  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT, color: COLOR.text, maxWidth: 760 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Crediti</h1>
      <p style={{ color: COLOR.muted, fontSize: 13, marginBottom: 24 }}>
        Utilizzo del mese corrente{activeTenant ? ` — ${activeTenant.display_name}` : ''}
      </p>

      {error && (
        <div role="alert" style={{
          marginBottom: 16, padding: '8px 12px', borderRadius: 6,
          border: `1px solid ${COLOR.errFg}`, color: COLOR.errFg,
          background: 'rgba(239,68,68,0.04)', fontSize: 12,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CreditBalanceCard balance={balance} showMoney={isSuperadmin} loading={loading} />
        <CreditUsageTable usage={usage} showMoney={isSuperadmin} loading={loading} />
      </div>
    </div>
  );
}
